/**
 * Logger utility that preserves stack traces and respects REACT_APP_ENABLE_DEBUG_LOGS flag
 *
 * Defaults to disabled (false) - only enabled when REACT_APP_ENABLE_DEBUG_LOGS is set to 'true'
 *
 * Usage:
 *   import { logger } from 'src/utils/logger'
 *
 *   logger.log('🔍 Debugging info')
 *   logger.info('✅ Success message')
 *   logger.warn('⚠️ Warning message')
 *   logger.error('❌ Error message')  // Always logged, even in prod
 *
 * Note: This implementation preserves stack traces in browser dev tools.
 */

const noop = () => {}

// Bind console methods once to preserve stack traces
const boundLog = console.log.bind(console)
const boundInfo = console.info.bind(console)
const boundWarn = console.warn.bind(console)
const boundError = console.error.bind(console)

// Check if debug logging is enabled via environment variable
const isDebugEnabled = process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true'

class Logger {
  /**
   * Log debug information - only when REACT_APP_ENABLE_DEBUG_LOGS is 'true'
   */
  log = isDebugEnabled ? boundLog : noop

  /**
   * Log informational messages - only when REACT_APP_ENABLE_DEBUG_LOGS is 'true'
   */
  info = isDebugEnabled ? boundInfo : noop

  /**
   * Log warnings - only when REACT_APP_ENABLE_DEBUG_LOGS is 'true'
   */
  warn = isDebugEnabled ? boundWarn : noop

  /**
   * Log errors - ALWAYS logged, even in production
   * Use this for actual errors that should be tracked
   */
  error = boundError

  /**
   * Log debug information - alias for log()
   */
  debug = this.log
}

export const logger = new Logger()
